// src/shared/geometry/lineClipping.js
// Cohen-Sutherland line clipping + THREE.js geometry segment extraction
import * as THREE from 'three';

// Region codes
const INSIDE = 0; // 0000
const LEFT   = 1; // 0001
const RIGHT  = 2; // 0010
const BOTTOM = 4; // 0100
const TOP    = 8; // 1000

function computeOutCode(x, y, minX, minY, maxX, maxY) {
  let code = INSIDE;
  if (x < minX) code |= LEFT;
  else if (x > maxX) code |= RIGHT;
  if (y < minY) code |= BOTTOM;
  else if (y > maxY) code |= TOP;
  return code;
}

/**
 * Cohen-Sutherland line clipping algorithm.
 * @returns {object|null} Clipped segment {x1,y1,x2,y2} or null if entirely outside
 */
export function clipLineSegment(x1, y1, x2, y2, minX, minY, maxX, maxY) {
  let outcode1 = computeOutCode(x1, y1, minX, minY, maxX, maxY);
  let outcode2 = computeOutCode(x2, y2, minX, minY, maxX, maxY);
  let accept = false;

  while (true) {
    if (!(outcode1 | outcode2)) {
      // Both inside
      accept = true;
      break;
    } else if (outcode1 & outcode2) {
      // Both in same outside region
      break;
    } else {
      // Partially outside - clip
      const outcodeOut = outcode1 ? outcode1 : outcode2;
      let x, y;

      if (outcodeOut & TOP) {
        x = x1 + (x2 - x1) * (maxY - y1) / (y2 - y1);
        y = maxY;
      } else if (outcodeOut & BOTTOM) {
        x = x1 + (x2 - x1) * (minY - y1) / (y2 - y1);
        y = minY;
      } else if (outcodeOut & RIGHT) {
        y = y1 + (y2 - y1) * (maxX - x1) / (x2 - x1);
        x = maxX;
      } else { // LEFT
        y = y1 + (y2 - y1) * (minX - x1) / (x2 - x1);
        x = minX;
      }

      if (outcodeOut === outcode1) {
        x1 = x;
        y1 = y;
        outcode1 = computeOutCode(x1, y1, minX, minY, maxX, maxY);
      } else {
        x2 = x;
        y2 = y;
        outcode2 = computeOutCode(x2, y2, minX, minY, maxX, maxY);
      }
    }
  }

  if (accept) {
    return { x1, y1, x2, y2 };
  }
  return null;
}

/**
 * Extract line segments from a THREE.js object's BufferGeometry.
 * Converts local coordinates to world coordinates.
 * @param {THREE.Object3D} obj
 * @returns {Array<{x1,y1,x2,y2}>}
 */
export function extractLineSegments(obj) {
  const segments = [];
  if (!obj.geometry) return segments;

  const posAttr = obj.geometry.attributes?.position;
  if (!posAttr) return segments;

  // Ensure world matrix is up to date
  obj.updateWorldMatrix(true, false);

  const v = new THREE.Vector3();
  const positions = [];
  for (let i = 0; i < posAttr.count; i++) {
    v.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
    v.applyMatrix4(obj.matrixWorld);
    positions.push({ x: v.x, y: v.y });
  }

  const index = obj.geometry.index;

  if (obj.isLineSegments) {
    // LineSegments: pairs of vertices
    if (index) {
      for (let i = 0; i < index.count; i += 2) {
        const a = positions[index.getX(i)];
        const b = positions[index.getX(i + 1)];
        if (a && b) segments.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
    } else {
      for (let i = 0; i < positions.length - 1; i += 2) {
        segments.push({ x1: positions[i].x, y1: positions[i].y, x2: positions[i + 1].x, y2: positions[i + 1].y });
      }
    }
  } else if (obj.isLine) {
    // Line or LineLoop: consecutive vertex pairs
    if (index) {
      for (let i = 0; i < index.count - 1; i++) {
        const a = positions[index.getX(i)];
        const b = positions[index.getX(i + 1)];
        if (a && b) segments.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
    } else {
      for (let i = 0; i < positions.length - 1; i++) {
        segments.push({ x1: positions[i].x, y1: positions[i].y, x2: positions[i + 1].x, y2: positions[i + 1].y });
      }
    }
  }

  return segments;
}

/**
 * Get the color of a THREE.js object as a hex number.
 */
function getObjectColor(obj) {
  if (obj.material) {
    const mat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
    if (mat && mat.color) return mat.color.getHex();
  }
  return 0xcccccc; // default gray
}

/**
 * Clip floor plan geometry to a rectangle, filtering out installation objects.
 * @param {THREE.Group} floorPlanGroup - The floor plan THREE.Group
 * @param {{minX,minY,maxX,maxY}} clipRect - Clipping rectangle in world coords
 * @returns {Array<{x1,y1,x2,y2,color}>} Clipped line segments
 */
export function clipFloorPlanGeometry(floorPlanGroup, clipRect) {
  const { minX, minY, maxX, maxY } = clipRect;
  const result = [];

  floorPlanGroup.traverse((obj) => {
    // Skip installation objects (layer 1, mask bit 1 = value 2)
    if ((obj.layers.mask & 2) !== 0) return;

    // Only process line-based objects
    if (!obj.isLine && !obj.isLineSegments) return;

    const color = getObjectColor(obj);
    const segments = extractLineSegments(obj);

    for (const seg of segments) {
      const clipped = clipLineSegment(seg.x1, seg.y1, seg.x2, seg.y2, minX, minY, maxX, maxY);
      if (clipped) {
        result.push({ ...clipped, color });
      }
    }
  });

  return result;
}
